import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { TEMPLATE_METRICS } from "@/pages/templates/template-checker/checker-config";
import { MetricProgress } from "@/pages/templates/template-checker/metric-progress";
import { templateFormSchema } from "@/schemas/template";
import { TemplateCheckResponse, TemplateMetrics } from "@/types/template.types";
import { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";
import DetailedReport from "./detailed-report"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface TemplateCheckerProps {
  form: UseFormReturn<z.infer<typeof templateFormSchema>>;
  templateId?: string;
  handleCheckTemplate: () => void;
  isChecking: boolean;
  metrics: Partial<TemplateMetrics>;
  setMetrics: (metrics: Partial<TemplateMetrics>) => void;
  report: TemplateCheckResponse;
  setReport: (report: TemplateCheckResponse) => void;
  setReportData: (report: TemplateCheckResponse) => void;
  reportDone: boolean;
}

// const sampleTemplateCheckResponse: TemplateCheckResponse = {
//   score: 85.5,
//   weights: {
//     spamWords: 0.3,
//     altAttributes: 0.15,
//     brokenLinks: 0.2,
//     dangerousHtml: 0.15,
//     shortenedUrls: 0.1,
//     readTime: 0.1,
//   },
//   countData: {
//     wordCount: {
//       upperCaseCount: 10,
//       wordCount: 200,
//     },
//     spamWords: {
//       details: {
//         Money: { words: [], count: 0 },
//         Shady: {
//           words: [
//             "free",
//             "win",
//             "prize",
//             "congratulations",
//             "winner",
//             "giveaway",
//             "offer",
//             "lucky",
//             "selected",
//             "exclusive",
//             "limited",
//             "claim",
//           ],
//           count: 18,
//         },
//         Urgency: { words: ["act fast"], count: 1 },
//         Overpromise: { words: [], count: 0 },
//         Unnatural: { words: [], count: 0 },
//         Phishing: { words: ["email", "scam"], count: 2 },
//         Sensationalism: { words: [], count: 0 },
//         Clickbait: { words: [], count: 0 },
//         SuspiciousLinks: { words: ["bit.ly"], count: 1 },
//       },
//       count: 22,
//     },
//     image: {
//       imageCount: 3,
//       invalidCount: 1,
//       emptyAltCount: 1,
//       invalidImages: [
//         {
//           src: "https://example.com/brokenimage1.jpg",
//           alt: null,
//           isValid: false,
//           issues: ["empty_alt"],
//         },
//         {
//           src: "https://example.com/brokenimage.jpg",
//           alt: null,
//           isValid: false,
//           issues: ["missing_alt"],
//         },
//       ],
//     },
//     links: {
//       count: 2,
//       details: [
//         {
//           href: "mailto example@gmail.com",
//           text: "Mail to this",
//           isValid: false,
//           reason: "missing_protocol",
//         },
//         {
//           href: "/brokenlink.com",
//           text: "this is broken link",
//           isValid: false,
//           reason: "missing_protocol",
//         },
//       ],
//     },
//     dangerousHtml: {
//       count: 1,
//       details: [{ type: "iframe", src: "https://xyz.com" }],
//     },
//     shortenedUrls: {
//       count: 1,
//       details: [{ url: "https://bit.ly/3xyz", service: "bit.ly" }],
//     },
//     questions: [
//       "What is the capital of France?",
//       "What is the capital of Germany?",
//     ],
//     readTime: { seconds: 26 },
//   },
// };

function TemplateChecker({
  form,
  templateId,
  isChecking,
  handleCheckTemplate,
  metrics,
  setMetrics,
  report,
  setReport,
  setReportData,
  reportDone,
}: TemplateCheckerProps) {
  const contentType = form.watch("contentType");

  // fetch will be disabled if templateId is not provided
  // const { data: oldReport, isFetched } = useGetTemplateReport({
  //   id: templateId ?? "",
  // });

  const getVisibleMetrics = useMemo(() => {
    return Object.entries(TEMPLATE_METRICS).filter(([_, config]) => {
      return !config.showCondition || config.showCondition(contentType);
    });
  }, [contentType]);

  // useEffect(() => {
  //   if (isFetched && oldReport) {
  //     setReportData(oldReport.report);
  //   }
  // }, [isFetched, oldReport]);

  useEffect(() => {
    if (report) {
      setReportData(report);
    }
  }, [report]);

  return (
    <Card className="shadow-none md:w-[200px] lg:w-[350px] h-fit">
      <CardHeader className="p-4">
        <CardTitle>Template Checker</CardTitle>
        <CardDescription>
          Check your template for any issues before saving.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full" viewBox="0 0 100 100">
                      <circle
                        className="stroke-gray-200"
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        strokeWidth="8"
                      />
                      <circle
                        className={cn(
                          "transition-all duration-1000 stroke-red-500",
                          (metrics.spamScore || 0) >= 90 && "stroke-green-500",
                          (metrics.spamScore || 0) >= 70 &&
                            (metrics.spamScore || 0) < 90 &&
                            "stroke-yellow-500"
                        )}
                        cx="50"
                        cy="50"
                        r="45"
                        fill="none"
                        strokeWidth="8"
                        strokeDasharray={`${
                          ((metrics.spamScore || 0) / 100) * 283 || 0
                        } 283`}
                        transform="rotate(-90 50 50)"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold">
                        {Math.round(metrics.spamScore || 0)}
                      </span>
                      <span className="text-sm text-gray-500">Score</span>
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="bg-white border shadow-md">
                  <p className="font-medium text-black">
                    Score: {Math.round(metrics.spamScore || 0)}/100
                  </p>
                  <p className={cn(
                    "text-sm",
                    (metrics.spamScore || 0) >= 90 
                      ? "text-emerald-600" 
                      : (metrics.spamScore || 0) >= 70 
                      ? "text-yellow-600" 
                      : "text-red-600"
                  )}>
                    {(metrics.spamScore || 0) >= 90 
                      ? "Excellent" 
                      : (metrics.spamScore || 0) >= 70 
                      ? "Good" 
                      : "Needs Improvement"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          {getVisibleMetrics?.map(([key, config]) => {
            if (key === "spamScore") return null;
            return (
              <MetricProgress
                key={key}
                value={metrics[key as keyof TemplateMetrics] || 0}
                config={config}
              />
            );
          })}
        </div>
        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            className="w-full"
            onClick={handleCheckTemplate}
            disabled={isChecking}
          >
            {isChecking ? "Checking..." : "Check Template"}
          </Button>
          {reportDone && <DetailedReport />}
        </div>
      </CardContent>
    </Card>
  );
}

export default TemplateChecker;
